import { makeAutoObservable, runInAction } from "mobx";
import {ACCESS_SCOPES, IOwnerVerificationStatus, IUser, USER_PLATFORM, USER_ACCESS_ENUM} from "../models/User";
import { TYPE, rootContainer } from "../services/container/root";
import PortalService from "../services/api/user-service";
import {isSet} from "../models/model-types-methods";

interface IUserChanges extends Partial<IUser> { };

class UserStore {
    private _changes: IUserChanges = {};
    private _model?: IUser; /** this can be undefined when logged in as sysadmin and not connected to any store */
    private _isSaving: boolean = false;
    private _isLoading: boolean = false;

    private get service(): PortalService {
        return rootContainer.get<PortalService>(TYPE.UserService);
    }

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private setModel(model?: IUser): void {
        this._model = model;
        this.discardChanges();
    }

    public discardChanges(): void {
        this._changes = {} as IUserChanges;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public getUpdatedModel(): IUser {
        return { ...this._model, ...this._changes } as IUser;
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    public get id() {
        return this._model?.id;
    }
    
    public get name() {
        return this._changes.name ?? this._model?.name;
    }

    public get displayName() {
        return this._changes.display_name ?? this._model?.display_name;
    }

    public getPortalName(): string|undefined {
        const platform = this._model?.platform;
        if (!platform) {
            return undefined;
        }
        return platform === USER_PLATFORM.PLATFORM_PORTAL ? this.displayName : 'Returns Portal';
    }
    
    public get planId() {
        return this._changes.plan_id ?? this._model?.plan_id;
    }
    
    public get partnerId() {
        if (this._changes.partner_id === null) {
            return null;
        }
        return this._changes.partner_id ?? this._model?.partner_id;
    }

    public get isLoading(): boolean { return this._isLoading; }


    /** These parameters can not be changed from RMS */
    public get setting() {
        return this._model?.setting;
    }
    
    public get platformService() {
        return this._model?.platform_service;
    }
    
    /** Not used after removing Cargo pop-up, but can be used in future for similar purpose */
    public get isSubscriptionsBlocked() {
        return this._model?.is_subscriptions_blocked;
    }
    
    public get hasNotifyRefundIntegration() {
        return this._model?.has_notify_refund_integration;
    }

    public get hasChildren() {
        return this._model?.has_children;
    }

    public get isChild() {
        return this._model?.is_child;
    }

    public get ownerVerificationStatus(): IOwnerVerificationStatus|undefined {
        /** checks if owner verified his email */
        return this._model?._shop_owner_verification_status;
    }

	public get missingAccessScopes(): ACCESS_SCOPES[] {
		return (this._model?.missing_scopes?.split(',')?.filter(Boolean) || []) as ACCESS_SCOPES[];
	}

	public get approveScopesRedirectUrl(): string|null {
		return this._model?._approve_scopes_redirect_url ?? null;
	}

    public get userAccess(): USER_ACCESS_ENUM | undefined {
        return this._model?.user_access;
    }

    public get hasMissingTranslations(): boolean | undefined { return this._model?._has_missing_translations; }

    /**
     * Global-e migrated portal not yet switched to ReturnGo on Global-e (banner on home).
     */
    public get showGlobaleActivationBanner(): boolean {
        return isSet(this._model?.global_e_user_id) && this._model?.global_e_is_rgo_activated !== true;
    }

    /** Non-Enterprise Global-e tier (null or any value other than Enterprise). */
    public get isGlobaleNonEnterpriseMerchantType(): boolean {
        return this._model?.global_e_merchant_type !== "Enterprise";
    }

    public get globaleAutomaticActivationScheduledAt(): string | null | undefined {
        return this._model?.globale_automatic_activation_scheduled_at;
    }

    public setPartnerId(v: string|null) {
        this._changes.partner_id = v;
    }

    public async load() {
        try {
            this._isLoading = true;

            const user = await this.service.getCurrentPortal();

            runInAction(async () => {
                this.setModel(user);    
                this._isLoading = false;
            });
        }
        catch(e) {
            runInAction(() => {
                this._isLoading = false;
            })
        }
    }

    public async save() {
        const updatedModel = this.getUpdatedModel();
        this._isSaving = true;

        try {
            const model = await this.service.updateCurrentPortal(updatedModel);
            runInAction(() => {
                this.setModel(model);
            });
        } catch (error) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

}

export default UserStore;
